import {PlayItem, TrickModeRestriction, ContentType, type Channel} from '@ca.vmedia.tv/domains';
import { TimeUtils } from "@/utils";

export class PlayItemRestrictionsProcessor {
    public hasRewind: boolean;
    public hasFastForward: boolean;
    public hasPause: boolean;
    public liveIsPausedOnce: boolean;

    constructor(
            private readonly playItem: PlayItem, 
            readonly restrictions: TrickModeRestriction,
            private readonly isRestartingStream: boolean, 
            private readonly isTimeFormat12: boolean,
            private readonly channel: Channel | null = null,
            private readonly locale: string
        ) {
        this.hasRewind = false;
        this.hasFastForward = false;
        this.hasPause = false;
        this.liveIsPausedOnce = false;
        this.init();
    }

    private init() {
        if (this.isLive) {
            this.hasRewind = false;
            this.hasFastForward = false;
            this.hasPause = this.hasRestartAvailable ? this.restrictions < TrickModeRestriction.Pause : false;
        } else {
            switch (this.restrictions) {
                case TrickModeRestriction.FastForward:
                    this.hasRewind = true;
                    this.hasFastForward = false;
                    this.hasPause = true;
                    break;
                case TrickModeRestriction.Rewind:
                    this.hasRewind = false;
                    this.hasFastForward = true;
                    this.hasPause = true;
                    break;
                case TrickModeRestriction.RewindFastForward:
                    this.hasRewind = false;
                    this.hasFastForward = false;
                    this.hasPause = true;
                    break;
                case TrickModeRestriction.Pause:
                    this.hasRewind = true;
                    this.hasFastForward = true;
                    this.hasPause = false;
                    break;
                case TrickModeRestriction.PauseFastForward:
                    this.hasRewind = true;
                    this.hasFastForward = false;
                    this.hasPause = false;
                    break;
                case TrickModeRestriction.RewindPause:
                    this.hasRewind = false;
                    this.hasFastForward = true;
                    this.hasPause = false;
                    break;
                case TrickModeRestriction.FastForwardRewindPause:
                    this.hasRewind = false;
                    this.hasFastForward = false;
                    this.hasPause = false;
                    break;

                default:
                    this.hasRewind = true;
                    this.hasFastForward = true;
                    this.hasPause = true;
                    break;
            }
        }
    }
    public get playItemId(): number | null {
        return this.playItem.recentAirTime?.id ?? this.playItem.id ?? null;
    }

    public get getRestrictions(): TrickModeRestriction {
        return this.restrictions;
    }

    public get lengthInS(): number | null {
        return this.playItem.lengthInS;
    }

    public get contentType(): ContentType | null {
        return this.playItem.contentType;
    }

    public get isRestart(): boolean {
        return this.isRestartingStream;
    }

    public get isLive(): boolean {
        if (this.isRestartingStream) {
            return false;
        }
        return PlayItem.isLive(this.playItem);
    }

    public get isVCloud(): boolean {
        return PlayItem.isVCloud(this.playItem, this.channel);
    }

    public get isVOD(): boolean {
        return (this.playItem.contentType === ContentType.SVOD || this.playItem.contentType === ContentType.VOD || this.playItem.contentType === ContentType.CraveTV);
    }

    public get isCrave(): boolean {
        return this.playItem.contentType === ContentType.CraveTV;
    }

    public get isCraveMovies(): boolean {
        return this.isCrave && this.playItem.showType?.toLowerCase() === "movie";
    }

    public get isVODSeries(): boolean {
        return this.isVOD && this.playItem.showType?.toLowerCase() === "series";
    }

    public get isVODMovies(): boolean {
        return this.isVOD && this.playItem.showType?.toLowerCase() === "movie";
    }

    public get hasNavBack(): boolean {
        return this.isVODSeries; // TODO: check channels
    }

    public get hasNavForward(): boolean {
        return this.isVODSeries; // TODO: check channels
    }

    public get hasRestart(): boolean {
        return this.isLive;
    }

    public get hasLive(): boolean {
        return this.isLive;
    }

    public get hasRestartAvailable(): boolean {
        return PlayItem.isRestartAvailable(this.playItem, this.channel);
    }

    public get title(): string {
        return this.playItem.name ?? this.playItem.shortName ?? "";
    }

    public get seriesName(): string {
        return this.playItem.seriesName ?? "";
    }

    public get seasonNumber(): string | number {
        return this.playItem.seasonNumber ?? "";
    }

    public get episodeNumber(): string | number {
        return this.playItem.episodeNum ?? "";
    }

    public get description(): string {
        return this.playItem.description  ?? "";
    }

    public get seriesDescription(): string {
        return this.playItem.seriesDescription  ?? "";
    }

    public get seasonDescription(): string {
        return this.playItem.seasonDescription  ?? "";
    }

    public get shortDescription(): string {
        return this.playItem.shortDescription  ?? "";
    }

    public get startTimeSec(): number {
        let _startTimeMs = null;
        
        if (this.playItem.itemDate) {
            _startTimeMs =  Number(this.playItem.itemDate.replace(/\D/g,''));
        } else if (this.playItem.recentAirTime) {
            _startTimeMs =  Number(this.playItem.recentAirTime.startTime);
        }

        return _startTimeMs ? _startTimeMs / 1000 : 0;
    }
    public get endTimeSec(): number {
        return !this.isVOD ? this.startTimeSec + (this.lengthInS ?? 0) : (this.lengthInS ?? 0);
    }

    public get startTimeFormatted(): string {
        let timeFormatted = '00:00';

        if (!this.isVOD) {
            const date = PlayItem.getStartTime(this.playItem);
            if (date) {
                const timeFormat = this.isTimeFormat12 ? 'hh:mm A' : 'HH:mm';
                timeFormatted = TimeUtils.getMoment(this.locale, date.getTime()).format(timeFormat);
            }
        }
        return timeFormatted;
    }

    public get endTimeFormatted(): string {
        let timeFormatted = '00:00';

        if (!this.isVOD) {
            const date = PlayItem.getFinishTime(this.playItem);
            if (date) {
                const timeFormat = this.isTimeFormat12 ? 'hh:mm A' : 'HH:mm';
                timeFormatted = TimeUtils.getMoment(this.locale, date.getTime()).format(timeFormat);
            }
        }

        if (this.isVOD && this.playItem.lengthInS || this.isCrave && this.playItem.lengthInS) {
            timeFormatted = this.formatTime(this.playItem.lengthInS, 2);
        }

        return timeFormatted;
    }

    public formatTime(seconds: number, format: number = 0): string {
        switch (format) {
            case 0:
                return TimeUtils.getMoment(this.locale, new Date(seconds * 1000).getTime()).format('mm:ss');

            case 1: {
                const momentFormat = this.isTimeFormat12 ? 'hh:mm:ss' : 'HH:mm:ss';
                return TimeUtils.getMoment(this.locale, new Date(seconds * 1000).getTime()).format(momentFormat);
            }
    
            case 2: {
                let s = Math.floor(seconds);
                let m = Math.floor(s / 60);
                const h = Math.floor(m / 60);
                
                m %= 60;
                s %= 60;
    
                return [
                    h > 0 ? this.formatComponent(h) : '00',
                    m > 0 ? this.formatComponent(m) : '00',
                    s > 9 ? s : `0${s}`
                ].join(':');
            }

            default:
                return '00:00';
        }
    }

    public formatComponent(value: number): string | number{
        return value > 9 ? value : `0${value}`;
    }

    public getProgress(currentTimeInS: number): number {
        if(!this.playItem.lengthInS) {
            return 0;
        }
        return Math.min(1, Math.max(0, currentTimeInS / this.playItem.lengthInS)) * 100;
    }

    public getTimeFromProgress(progress: number): number {
        if(!this.playItem.lengthInS) {
            return 0;
        }
        return Math.round(Math.min(1, Math.max(0, progress / 100)) * this.playItem.lengthInS);
    }
}